import Vue from "vue"
import _ from "lodash"

export let defaultThreadSettings = {
	numbering: {
		enable: false,
		prefix: true,
		format: '[n]/',
		skipFirst: false,
		skipLast: false,
	}
}
let makeThread = function () {
	return {
		title: '',
		notes: '',
		tweets: [makeTweet()],
		replyTo: null,
		activeTweet: 0,
		last_edit: new Date(),
		settings: _.cloneDeep(defaultThreadSettings)
	}
}

let getId = function () {
	// get a 10 char random string - https://stackoverflow.com/questions/1349404/generate-random-string-characters-in-javascript
	return (Math.random().toString(36) + '00000000000000000').slice(2, 12)
}

let makeTweet = function (text) {
	return {
		id: getId(),
		text: text || '',
		image_ids: [], // text ID of the image in our global image map
		mentions: [],
		urls: [],
		hasFocus: false,
		error: Vue.prototype.$tweetErrorEnum.EMPTY,
		quote_tweet: undefined,
		smart_paste: undefined, // temporary hold for a new tweet with smart paste text
	}
}

Vue.prototype.$tweetErrorEnum = Object.freeze({
	EMPTY: 'is empty',
	TOO_LONG: 'is too long',
	TOO_MANY_IMAGES: 'has more than four images',
	TOO_MANY_GIFS: 'has more than one animated gif',
	GIFS_MIXED_WITH_IMAGES: 'has a mix of images and animated gifs',
})


export default {
	state: {
		threads: [],
		lastDeletedTweet: null,
		activeThread: null,
	},
	
	reducer: (state) => ({
		threads: state.compose.threads,
		activeThread: state.compose.activeThread,
	}),
	
	getters: {
		threadsByLastEdit: (state) => {
			// get a copy of threads, set the index to match the original index for later lookups,
			// then return it sorted by last edit date
			let threadsCopy = [...state.threads]
			threadsCopy.forEach((thread, index) => {
				thread.index = index
			})
			return threadsCopy.sort((a, b) => b.last_edit - a.last_edit)
		},
		
		/* proxy for all state calls - actually get the value from the active thread */
		activeThread: (state) => {
			return state.threads[state.activeThread]
		},
		
		tweets: (state) => {
			return state.threads[state.activeThread].tweets
		},
		
		composeSettings: (state) => (section, property) => {
			return state.threads[state.activeThread].settings[section][property]
		},
		
		totalImagesInActiveThread: (state, getters) => {
			return getters.tweets.reduce((accumulator, tweet) => accumulator + tweet.image_ids.length, 0)
		},
		
		replyTo: (state) => {
			return state.threads[state.activeThread].replyTo
		},
		
		threadEmpty: (state) => (index) => {
			return state.threads[index].tweets.length === 1 &&
							state.threads[index].title === '' &&
							state.threads[index].tweets[0].error === Vue.prototype.$tweetErrorEnum.EMPTY
		},
		
		/* getters which use the above */
		activeThreadEmpty: (state, getters) => {
			return getters.threadEmpty(state.activeThread)
		},
		
		/* not just a 1-tweet, empty thread, but one without a reply to or quote tweet */
		activeThreadUntouched: (state, getters) => {
			return getters.threadEmpty(state.activeThread) &&
				(getters.replyTo === null) &&
				(getters.tweets[0].quote_tweet === undefined)
		},
		
		tweetThreadIsValid: (state, getters) => {
			return getters.tweets.every(tweet => tweet.error === null)
		},
		
		tweetThreadErrors: (state, getters) => {
			return getters.tweets.filter(tweet => tweet.error !== null)
		},
		
		firstTweetError: (state, getters) => {
			return getters.tweetThreadErrors[0]
		},
		
		canPostThread: (state, getters) => {
			return Boolean(getters.tweetThreadIsValid && !getters.activeThreadEmpty && getters.isLoggedIn)
		},
		
		tweetById: (state, getters) => (id) => {
			return getters.tweets.find(tweet => tweet.id === id)
		},
		
		tweetIndexById: (state, getters) => (id) => {
			return getters.tweets.findIndex(tweet => tweet.id === id)
		},
		
		tweetImages: (state, getters, rootState) => (id) => {
			// lookup each image in the indexes - we skip those that dont map, indicates
			// the image doesn't exist
			return getters.tweets[id].image_ids.map(val => rootState.images.images[val]).filter(val => val !== undefined)
		},
		
		replyingToOther: (state, getters, rootState) => {
			return getters.replyTo !== null &&
				getters.replyTo.user &&
				rootState.auth.profile &&
				getters.replyTo.user.screen_name !== rootState.auth.profile.screen_name
		},
		
		tweetNumbering: (state, getters) => (index, prefix) => {
			/*
			Returns the numbering text, if number is enabled and prefix boolean
			in settings matches what's passed in.
			
			Formatting will take [n] and [total]
			 */
			if (
				getters.composeSettings('numbering', 'enable') &&
				getters.composeSettings('numbering', 'prefix') === prefix) {
				
				let skipFirst = getters.composeSettings('numbering', 'skipFirst');
				let skipLast = getters.composeSettings('numbering', 'skipLast');
				
				// work out total tweets, minus 1 if skipping first or last
				let tweetTotal = getters.tweets.length - skipFirst - skipLast;
				
				// this tweet index, plus 1 (for 1-based), minus one if skipping first
				let tweetPos = (index + 1) - skipFirst;
				
				// if we don't have another total tweets, or we are the first or last, no prefix
				if (tweetTotal <= 0 || tweetPos < 1 || tweetPos > tweetTotal) {
					return ''
				}
				
				let fmt = getters.composeSettings('numbering', 'format')
				fmt = fmt.replace('[n]', tweetPos).replace('[total]', tweetTotal)
				fmt = (prefix ? '' : ' ') + fmt + (prefix ? ' ' : '')
				return fmt
			}
			return ''
		},
		
		replyPrefix: (state, getters) => (index) => {
			/*
			if this is the first tweet in the thread, and we are doing a reply-to that
			is to a different screen name, we need to prefix with `@{name} `
			*/
			return (index === 0 && getters.replyingToOther)
				? `@${getters.replyTo.user.screen_name} `
				: ''
		},
		
		
		fullTweetText: (state, getters) => (index) => {
			return getters.tweetPrefix(index) + getters.tweets[index].text + getters.tweetSuffix(index)
		},
		
		tweetPrefix: (state, getters) => (index) => {
			return getters.replyPrefix(index) + getters.tweetNumbering(index, true)
		},
		
		tweetSuffix: (state, getters) => (index) => {
			return getters.tweetNumbering(index, false)
		},
		
		getQuoteTweet: (state, getters) => (index) => {
			return getters.tweets[index].quote_tweet
		},
		
		getQuoteTweetUrl: (state, getters) => (index) => {
			let tweet = getters.tweets[index].quote_tweet
			if (!tweet) {
				return ''
			}
			return `https://twitter.com/${tweet.user.screen_name}/status/${tweet.id}/`
		},
		
		focusedTweetIndex: (state, getters) => {
			return getters.tweets.findIndex(tweet => tweet.hasFocus);
		},
		
		activeTweetIndex: (state) => {
			return state.threads[state.activeThread].activeTweet
		},
		
		getThreadTitle: (state) => (index) => {
			let thread = state.threads[index]
			
			return thread.title.trim() ||
				(thread.replyTo && `Re. @${thread.replyTo.user.screen_name}`) ||
				(thread.tweets[0].quote_tweet && `RT @${thread.tweets[0].quote_tweet.user.screen_name}`) ||
				thread.tweets[0].text.trim() ||
				'(Thread ' + (index + 1).toString() + ')'
		},
		
		emptyThreadIndex: (state, getters) => {
			// This 'reduces' the threads to the index of the first one that is empty
			// or -1 if an empty thread doesn't exist.
			return state.threads.reduce((accumulator, thread, index) => {
				if (getters.threadEmpty(index)) {
					return index
				}
			}, -1)
		},
		
		nonEmptyThreadCount: (state, getters) => {
			return state.threads.filter((thread, index) => !(getters.threadEmpty(index))).length
		}
		
	},
	
	mutations: {
		
		refreshLastEdit(state) {
			state.threads[state.activeThread].last_edit = new Date()
		},
		
		insertTweetFromData(state, {index, data}) {
			state.threads[state.activeThread].tweets.splice(index, 0, data)
		},
		
		insertTweet(state, {index, text, hasFocus}) {
			let tweet = makeTweet(text);
			tweet.hasFocus = hasFocus || false;
			state.threads[state.activeThread].tweets.splice(index, 0, tweet)
		},
		
		appendTweet(state, {text, hasFocus}) {
			let tweet = makeTweet(text)
			tweet.hasFocus = hasFocus || false;
			state.threads[state.activeThread].tweets.push(tweet)
		},
		
		setSmartPaste(state, {index, text}) {
			state.threads[state.activeThread].tweets[index].smart_paste = text
		},
		
		// changes entire array of tweets in active thread
		setAllTweets(state, tweets) {
			state.threads[state.activeThread].tweets = tweets
		},
		
		setActiveTweet(state, index) {
			state.threads[state.activeThread].activeTweet = index
		},
		
		setLastDeletedTweet(state, {index, thread, data}) {
			state.lastDeletedTweet = {index, thread, data}
		},
		
		deleteTweet(state, index) {
			state.threads[state.activeThread].tweets.splice(index, 1);
		},
		
		deleteTweets(state, {index, count}) {
			state.threads[state.activeThread].tweets.splice(index, count)
		},
		
		setTweetText(state, {index, text}) {
			state.threads[state.activeThread].tweets[index].text = text;
		},
		
		setTweetMentions(state, {index, mentions}) {
			state.threads[state.activeThread].tweets[index].mentions = mentions;
		},
		
		setTweetUrls(state, {index, urls}) {
			state.threads[state.activeThread].tweets[index].urls = urls;
		},
		
		/* image changes - this does no checking etc - we should use the actions, not these direcly */
		linkImage(state, {tweet_index, image_id}) {
			state.threads[state.activeThread].tweets[tweet_index].image_ids.push(image_id)
		},
		
		unlinkImage(state, {tweet_index, image_id}) {
			let image_ids = state.threads[state.activeThread].tweets[tweet_index].image_ids
			image_ids.splice(image_ids.indexOf(image_id), 1)
		},
		
		setTweetFocus(state, {index, val}) {
			if (state.threads[state.activeThread].tweets[index]) {
				state.threads[state.activeThread].tweets[index].hasFocus = val;
			}
		},
		
		setTweetError(state, {index, error}) {
			state.threads[state.activeThread].tweets[index].error = error
		},
		
		clearReplyTo(state) {
			state.threads[state.activeThread].replyTo = null
		},
		
		setReplyTo(state, data) {
			state.threads[state.activeThread].replyTo = data
		},
		
		clearQuoteTweet(state, index) {
			state.threads[state.activeThread].tweets[index].quote_tweet = undefined
		},
		
		setQuoteTweet(state, {index, data}) {
			state.threads[state.activeThread].tweets[index].quote_tweet = data
		},
		
		/* thread controls */
		insertThread(state, index) {
			let thread = makeThread()
			state.threads.splice(index, 0, thread)
		},
		
		deleteThread(state, index) {
			state.lastDeletedTweet = null;
			state.threads.splice(index, 1);
		},
		
		setThreadTitle(state, {index, text}) {
			state.threads[index].title = text.trim()
		},
		setThreadNotes(state, {index, text}) {
			state.threads[index].notes = text.trim()
		},
		
		replaceComposeSettings(state, {index, data}) {
			state.threads[index].settings = data
		},
		
		setComposeSetting(state, {index, section, property, value}) {
			state.threads[index].settings[section][property] = value
		}
		
		
	},
	
	actions: {
		
		switchActiveThread({state, getters}, index) {
			// only switch if index is within bounds
			if (index < state.threads.length) {
				state.activeThread = index
			}
		},
		
		switchActiveTweet({state, commit, getters}, index) {
			if (index < getters.tweets.length) {
				commit('setActiveTweet', index)
			}
		},
		
		focusOnActiveTweet({getters, dispatch}) {
			dispatch('focusOnTweet', getters.activeTweetIndex)
		},
		
		
		setComposeSetting({state, commit}, {section, property, value}) {
			let index = state.activeThread
			commit('setComposeSetting', {index, section, property, value})
		},
		
		createNewThread({state, getters, commit, dispatch}) {
			// if we already have a blank thread, switch to that instead of creating another blank
			if (getters.emptyThreadIndex > -1) {
				dispatch('switchActiveThread', getters.emptyThreadIndex)
			} else {
				let index = state.threads.length;
				commit('insertThread', index)
				dispatch('switchActiveThread', index)
			}
		},
		
		removeThread({state, commit, dispatch}, index) {
			commit('deleteThread', index)
			dispatch('checkOrphanImages')
			if (state.threads.length === 0) {
				dispatch('createNewThread')
			} else if (state.activeThread > index) {
				dispatch('switchActiveThread', state.activeThread - 1)
				
			} else if (state.activeThread === index) {
				// if we deleted the last one and we're on it, move down one
				if (index === state.threads.length) {
					dispatch('switchActiveThread', index - 1)
				}
			}
		},
		
		checkOrphanImages({state, commit, rootState}) {
			// checks all images and deletes them if they aren't mapped to
			// any tweet on any thread
			let all_mapped_image_ids = []
			state.threads.forEach(thread => {
				thread.tweets.forEach(tweet => {
					all_mapped_image_ids += tweet.image_ids
				})
			})
			_.forEach(rootState.images.images, image => {
				if (all_mapped_image_ids.indexOf(image.id) === -1) {
					commit('removeImage', image.id, {root: true})
				}
			})
		},
		
		removeActiveThread({state, dispatch}) {
			dispatch('removeThread', state.activeThread)
		},
		
		addTweet({commit}, {index, text}) {
			commit('insertTweet', {index: index, text: text, hasFocus: false})
			commit('refreshLastEdit')
		},
		
		defocusTweet({commit, getters}) {
			if (getters.focusedTweetIndex > -1) {
				commit('setTweetFocus', {index: getters.focusedTweetIndex, val: false})
			}
		},
		
		addTweetWithSmartPaste({commit, getters}, {index, text}) {
			let tweet = makeTweet('')
			tweet.smart_paste = text
			commit('insertTweetFromData', {index: index, data: tweet, hasFocus: false})
		},
		
		addTweetAndFocus({commit, dispatch}, {index, text}) {
			dispatch('defocusTweet');
			commit('insertTweet', {index: index, text: text, hasFocus: true})
			commit('setActiveTweet', index)
			commit('refreshLastEdit')
		},
		
		deleteTweet({commit, state, getters, dispatch}, index) {
			// all tweet deletion should run through this - ensures images are de-linked correctly
			let image_ids = getters.tweets[index].image_ids.slice()
			commit('setLastDeletedTweet', {
				index: index,
				thread: state.activeThread,
				data: _.cloneDeep(getters.tweets[index])
			})
			commit('deleteTweet', index);
			image_ids.forEach(image_id => dispatch('removeImageIfUnused', image_id))
			commit('refreshLastEdit')
		},
		
		removeTweet({commit, getters, dispatch}, index) {
			dispatch('deleteTweet', index);
			if (!getters.tweets.length) {
				commit('insertTweet', {index: 0, text: ''});
			}
			
			// focus on the next tweet, or the one above if we deleted the last one
			dispatch('focusOnTweet', index === getters.tweets.length ? index - 1 : index)
			commit('refreshLastEdit')
		},
		
		clearTopTweets({commit, getters}, count) {
			commit('deleteTweets', {index: 0, count: count})
			if (!getters.tweets.length) {
				commit('insertTweet', {index: 0, text: ''});
			}
			commit('refreshLastEdit')
		},
		
		insertTextToTweet({commit, getters}, {index, text}) {
			commit('setTweetText', {
				index: index,
				text: text + getters.tweets[index].text
			});
			commit('refreshLastEdit')
		},
		
		changeTweetText({commit}, {index, text}) {
			commit('setTweetText', {index, text})
			commit('refreshLastEdit')
		},
		
		setTweetError({commit}, {index, error}) {
			commit('setTweetError', {index: index, error: error})
		},
		
		focusOnTweet({commit, getters, dispatch}, index) {
			dispatch('defocusTweet');
			
			// this wraps the index, so if going 'above' the first, wwrap to bottom; if going below
			// the bottom, wrap to the first one
			if (index === -1) {
				index = getters.tweets.length - 1;
			}
			if (index === getters.tweets.length) {
				index = 0;
			}
			commit('setTweetFocus', {index: index, val: true})
			commit('setActiveTweet', index)
		},
		
		/* images - interfaces with the following mutations
		linkImage(state, {tweet_index, image_id})
		unlinkImage(state, {tweet_index, image_id})

		- on state.images...
			addImage(state, {image_data})
			removeImage(state, {image_id})
		*/
		
		removeImageIfUnused({state, commit, getters}, image_id) {
			// first check it's not in an undeleted tweet
			if (state.lastDeletedTweet &&
				_.includes(state.lastDeletedTweet.data.image_ids, image_id)) {
				return
			}
			
			// then check if there are no tweets linked to the image, delete it
			if (getters.tweets.filter(tweet => _.includes(tweet.image_ids, image_id)).length === 0) {
				commit('removeImage', image_id, {root: true})
			}
		},
		
		addExistingImageToTweet({commit}, {tweet_index, image_id}) {
			commit('linkImage', {tweet_index, image_id})
			commit('refreshLastEdit')
		},
		
		addNewImageToTweet({commit, getters}, {tweet_index, image_data}) {
			let image_id = image_data.id = getId()  // add to object as well for future ref
			commit('addImage', image_data, {root: true})
			commit('linkImage', {tweet_index, image_id})
			commit('refreshLastEdit')
		},
		
		removeImageFromTweet({commit, dispatch}, {tweet_index, image_id}) {
			commit('unlinkImage', {tweet_index, image_id})
			dispatch('removeImageIfUnused', image_id)
		},
		
		undeleteTweet({state, commit, getters, dispatch}) {
			// if the thread is empty, replace it with the delete one so we don't end up
			// with a blank
			let tweet = state.lastDeletedTweet
			if (getters.activeThreadEmpty) {
				commit('setAllTweets', [tweet.data])
				dispatch('focusOnTweet', 0)
			} else {
				commit('insertTweetFromData', tweet)
				dispatch('focusOnTweet', tweet.index)
			}
			commit('refreshLastEdit')
		}
		
	}
}
